/**
 * Bulk reads of v_entity_type_effective_fields — DEC-0409 acceptance criterion 1
 * (docs/specs/universal-instance-contract.md): every lens sees the same
 * effective field set.
 *
 * fetchEffectiveFieldRefs(slug) serves a lens that shows one type. Surfaces
 * that touch many types at once — matching across types, the "types using a
 * field" reverse lookup — read the whole view once and group it here, instead
 * of falling back to entity_types.field_refs, which misses every inherited ref.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { EffectiveFieldRef } from './instanceResolver';

export const EFFECTIVE_FIELDS_VIEW = 'v_entity_type_effective_fields';

/** One row of the view, keyed by the type it belongs to. */
export interface EffectiveFieldRow {
  slug: string;
  field_ref: string;
  from_layer: string;
  depth: number;
}

/** PostgREST caps a response (1000 rows by default); page until a short page. */
const PAGE_SIZE = 1000;

/**
 * Every row of the view, all types. Returns null on error so the caller can
 * keep its own fallback — an empty array would read as "no type claims anything".
 */
export async function fetchEffectiveFieldRows(
  client: SupabaseClient,
): Promise<EffectiveFieldRow[] | null> {
  const rows: EffectiveFieldRow[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await client
      .from(EFFECTIVE_FIELDS_VIEW)
      .select('slug, field_ref, from_layer, depth')
      .order('slug', { ascending: true })
      .order('depth', { ascending: true })
      .order('field_ref', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) { console.error('fetchEffectiveFieldRows:', error.message); return null; }
    const page = (data ?? []) as EffectiveFieldRow[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

/**
 * slug → effective refs, depth ascending within each type — the exact shape
 * fetchEffectiveFieldRefs(slug) returns, so resolveEffectiveFields treats a
 * bulk read and a single read identically.
 */
export function groupEffectiveRefsBySlug(
  rows: readonly EffectiveFieldRow[],
): Map<string, EffectiveFieldRef[]> {
  const bySlug = new Map<string, EffectiveFieldRef[]>();
  for (const r of rows) {
    const list = bySlug.get(r.slug) ?? [];
    list.push({ ref: r.field_ref, from_layer: r.from_layer, depth: r.depth });
    bySlug.set(r.slug, list);
  }
  for (const list of bySlug.values()) list.sort((a, b) => a.depth - b.depth);
  return bySlug;
}

/**
 * Reverse lookup: meta_key → slugs of every type that carries it, directly or
 * by inheritance. A type appears once per field even when it both claims and
 * inherits it. `excludeSlugs` mirrors fetchEntityTypes, which hides `dev`.
 */
export function groupFieldUsage(
  rows: readonly EffectiveFieldRow[],
  excludeSlugs: readonly string[] = ['dev'],
): Record<string, string[]> {
  const excluded = new Set(excludeSlugs);
  const usage = new Map<string, Set<string>>();
  for (const r of rows) {
    if (excluded.has(r.slug)) continue;
    const slugs = usage.get(r.field_ref) ?? new Set<string>();
    slugs.add(r.slug);
    usage.set(r.field_ref, slugs);
  }
  return Object.fromEntries([...usage].map(([key, slugs]) => [key, [...slugs]]));
}
